import { useState } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import type { ConversionCreditWithAmounts } from '@sorare/core/src/hooks/useConversionCredits';
import { useConversionCredits } from '@sorare/core/src/hooks/useConversionCredits';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

type Props = {
  sport: Sport;
  currentlyUsedConversionCredit?: ConversionCreditWithAmounts;
  canUseConversionCredit: boolean;
  amount: MonetaryAmount;
  applyConversionCredits: boolean;
};

export const useConversionCreditSelector = ({
  sport,
  currentlyUsedConversionCredit,
  canUseConversionCredit,
  amount,
  applyConversionCredits,
}: Props) => {
  const { lifecycle } = useLifecycle();
  const {
    flags: { useAllAvailableCredits = false },
  } = useFeatureFlags();
  const [useAvailableCredits, setUseAvailableCredits] = useState<boolean>(
    applyConversionCredits
  );
  const { conversionCredits, groupedConversionCredits, loading } =
    useConversionCredits({
      sport,
      currentlyUsedConversionCredit,
      forceRefetch: canUseConversionCredit,
    });

  const availableConversionCredits = conversionCredits?.filter(
    cc => !cc.disabled
  );

  const [selectedConversionCredits, setSelectedConversionCredits] = useState<
    ConversionCreditWithAmounts[]
  >([]);

  const [conversionCreditInitialized, setConversionCreditInitialized] =
    useState<boolean>(false);

  if (
    !loading &&
    !conversionCreditInitialized &&
    availableConversionCredits?.length &&
    !lifecycle?.unselectConversionCreditByDefaultFromSettings
  ) {
    setConversionCreditInitialized(true);
    setSelectedConversionCredits(
      applyConversionCredits ? availableConversionCredits || [] : []
    );
  }

  return {
    showConversionCredit:
      amount.isPositive &&
      canUseConversionCredit &&
      availableConversionCredits &&
      availableConversionCredits?.length > 0,
    conversionCredits,
    groupedConversionCredits,
    selectedConversionCredits:
      canUseConversionCredit && !useAllAvailableCredits
        ? selectedConversionCredits
        : [],
    setSelectedConversionCredits: useAllAvailableCredits
      ? () => {}
      : setSelectedConversionCredits,
    useAvailableCredits: canUseConversionCredit && useAvailableCredits,
    setUseAvailableCredits: useAllAvailableCredits
      ? setUseAvailableCredits
      : () => {},
  };
};
